/* eslint-disable */
import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import moment from 'moment';
import { GENERAL_INPUT_STYLE_DATE } from './styles/index';
import PropTypes from 'prop-types';
import { getContext } from 'common';
import CalendarIcon from '@nokia-csf-uxr/ccfk-assets/CalendarIcon';
import Button from 'components/Button';
import TextInput, { TextInputButton } from '../../../TextInput';
import Calendar from 'components/Calendar';
import {
  CalendarContent,
  CalendarHeader,
  CalendarDayPicker,
  CalendarMonthPicker,
  CalendarFooter,
  CalendarYearPicker,
} from '../../../Calendar';

/**
 *  FloatingFilter is a custom cellRenderer component for floating filters
 *  It is for to achieve the hover and focus border effect.
 */
const FloatingFilter = forwardRef(({
  parentFilterInstanceCallback, onParentModelChangeCallback, inputProps, ...props
}, ref) =>
  getContext(({ RTL, theme }) => {
    const textInputButtonRef = useRef(null);
    const calendarFieldRef = useRef(null);
    const popupRef = useRef(null);
    const yearButtonRef = useRef(null);

    const [currentValue, setCurrentValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isYearPickerOpen, setIsYearPickerOpen] = useState(false);

    /** Field value */
    const getMomentFromField = fieldValue => moment(fieldValue, FIELD_FORMAT, true);

    const STATE_FORMAT = 'dddd, MMMM D YYYY, h:mm:ss A';
    const HEADER_YEAR_FORMAT = 'YYYY';
    const HEADER_FORMAT = 'ddd, MMM DD';
    const FIELD_FORMAT = 'YYYY-MM-DD';

    // Use this hook for DataGrid related lifecycle methods. For details see: https://www.ag-grid.com/documentation/react/react-hooks/
    useImperativeHandle(ref, () => ({
      onParentModelChanged(parentModel) {
        onParentModelChangeCallback && onParentModelChangeCallback(parentModel);
      }
    }));

    useEffect(() => {
    // This is usually used for synchronizing the value of Floating filter with to the column filter. For example searching text in Floating filter will appear in column filter as well.
    // For more details please read: https://www.ag-grid.com/react-grid/component-floating-filter/#ifloatingfilterparams
      props.parentFilterInstance(instance => parentFilterInstanceCallback && parentFilterInstanceCallback(instance, currentValue));
    }, [currentValue]);

    useEffect(() => {
      if (!isYearPickerOpen) {
        yearButtonRef && yearButtonRef.current && yearButtonRef.current.focus();
      }
    }, [isYearPickerOpen]);

    const valueChanged = (event) => {
      setCurrentValue(event.target.value);
    };

    /** Selected Date */
    const [formattedDate, setDate] = useState(undefined);
    const date = formattedDate !== undefined ? new Date(formattedDate) : undefined;
    const setFormattedDate = newDate => setDate(newDate.format(STATE_FORMAT));
    const momentDate = date !== undefined ? moment(date) : undefined;

    /** Header values */
    const year = momentDate ? momentDate.format(HEADER_YEAR_FORMAT) : undefined;
    const header = momentDate ? momentDate.format(HEADER_FORMAT) : undefined;

    /** Visible date on the DayPicker and  MonthPicker  */
    const [formattedVisibleDate, setVisibleDate] = useState(moment().format(STATE_FORMAT));
    const visibleDate = new Date(formattedVisibleDate);
    const setFormattedVisibleDate = newVisibleDate => setVisibleDate(newVisibleDate.format(STATE_FORMAT));
    const momentVisibleDate = moment(visibleDate);

    const yearProps = {
      disabled: isYearPickerOpen,
      value: year,
      onClick: () => { setIsYearPickerOpen(true); },
      ref: yearButtonRef
    };

    const calendarProps = {
      popupProps: {
        triggerEl: calendarFieldRef,
        isOpen,
        ref: popupRef
      }
    };

    const handleDateChange = (newDate) => {
      const newMomentDate = moment(newDate);
      setFormattedDate(newMomentDate);
      setFormattedVisibleDate(newMomentDate);
      setCurrentValue(newMomentDate.format(FIELD_FORMAT));
    };

    const handleYearPickerChange = ({ year })=> {
      setIsYearPickerOpen(false);
      handleDateChange(momentDate.set('year', year));
    }

    const handleCancelButton = () => {
      setIsOpen(false);
      setDate(formattedDraftDate);
      if (formattedDraftDate === undefined) {
        setCurrentValue("");
      } else {
        setCurrentValue(moment(formattedDraftDate).format(FIELD_FORMAT));
      }
      textInputButtonRef.current.focus();
    }

    const handleOkButton = () => {
      setIsOpen(false);
      textInputButtonRef.current.focus();
    }

    return (
        <>
          <TextInput
            ref={calendarFieldRef}
            placeholder={props.column.colDef.filter === "agDateColumnFilter" ? "YYYY-MM-DD" : ""}
            theme={theme.DATAGRID}
            style={ GENERAL_INPUT_STYLE_DATE }
            value={currentValue}
            onChange={valueChanged}
            type={props.column.colDef.filter === "agDateColumnFilter" ? "date" : ""}
            compactMode={props.api.context.compactMode}
            button={props.column.colDef.filter === "agDateColumnFilter" &&
              <div style={{ marginLeft: '0.25rem' }}>
                <TextInputButton
                  ref={textInputButtonRef}
                  onBlur={event => { event.stopPropagation(); /* To prevent field from triggering onBlur */ }}
                  onClick={() => {
                    setIsOpen(!isOpen);
                  }}
                >
                  <CalendarIcon />
                </TextInputButton>
              </div>
            }
          />
          <Calendar {...calendarProps}>
            <CalendarHeader year={yearProps} header={header} />
            <CalendarContent>
              <CalendarMonthPicker
                visibleMonth={momentVisibleDate.format('MMMM YYYY')}
                onChange={({ updateMonthWith }) => { setFormattedVisibleDate(momentVisibleDate.add(updateMonthWith, 'month')) } }
              />
              <CalendarDayPicker
                visibleDate={visibleDate}
                onDaySelect={({ day }) => handleDateChange(day)}
                date={date}
              />
              {date && isYearPickerOpen && <CalendarYearPicker onChange={handleYearPickerChange} visibleYear={momentVisibleDate.year()} />}
            </CalendarContent>
            <CalendarFooter>
              <Button onClick={handleCancelButton}>CANCEL</Button>
              <Button onClick={handleOkButton}>OK</Button>
            </CalendarFooter>
          </Calendar>
        </>
    );
  }));

FloatingFilter.propTypes = {
  /* Props just for FloatingFilterInput */
  inputProps: PropTypes.shape({}),
  /* The Grid API */
  api: PropTypes.shape({}),
  /* Custom callback function to get instance reference of the parent filter.
   * For more details about how to use it please read: https://www.ag-grid.com/react-grid/component-floating-filter/#floating-filter-methods-on-provided-filters
   */
  parentFilterInstanceCallback: PropTypes.func,
  /* Custom callback function for onParentModelChanged callback of Floating Filter Interface.
   * For more details about how to use it please read: https://www.ag-grid.com/react-grid/component-floating-filter/#floating-filter-interface-ifloatingfiltercomp
   */
  onParentModelChangeCallback: PropTypes.func
};
FloatingFilter.defaultProps = {
  inputProps: {},
  api: {},
  parentFilterInstanceCallback: undefined,
  onParentModelChangeCallback: undefined
};
FloatingFilter.displayName = 'FloatingFilter';
export default FloatingFilter;
